import { useMemo } from "react";
import { ArrowDown, ArrowUp, Bot, Plus, Trash2, Workflow } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Field, Input, NativeSelect, Textarea } from "@ncp/ui";
import { RefPicker, type RefOption } from "../workspaces/RefPicker";
import { useAgentCatalog, type AgentBody, type AgentBuilderOptions, type AgentStep } from "./api";

type Patch = (p: Partial<AgentBody>, opts?: { transient?: boolean }) => void;

/** Steps of a multi-step agent and the sub-agents it may ask; the runtime runs the steps in order, then writes the draft from what they produced. */
export function StepsTab({ agentKey, body, patch, readOnly, options }: { agentKey: string; body: AgentBody; patch: Patch; readOnly: boolean; options?: AgentBuilderOptions }) {
  const { t } = useTranslation();
  const catalog = useAgentCatalog();
  const agents = useMemo<RefOption[]>(() => (catalog.data ?? []).filter((a) => a.key !== agentKey && a.enabled).map((a) => ({ key: a.key, name: a.name, nameAr: a.nameAr, hint: a.key, description: a.description })), [catalog.data, agentKey]);
  const tools = useMemo<RefOption[]>(() => (options?.tools ?? []).map((x) => ({ key: x.key, name: x.key, description: x.purpose })), [options?.tools]);
  const steps = body.steps;
  const set = (next: AgentStep[], transient = false) => patch({ steps: next }, { transient });
  const edit = (i: number, p: Partial<AgentStep>, transient = false) => set(steps.map((s, j) => (j === i ? { ...s, ...p } : s)), transient);
  const move = (i: number, d: -1 | 1) => { const next = [...steps]; const [s] = next.splice(i, 1); next.splice(i + d, 0, s!); set(next); };
  const add = () => set([...steps, { key: `step-${steps.length + 1}`, title: t("admin:agents.steps.newTitle", { n: steps.length + 1 }), instruction: "", toolKeys: [], delegateAgentKey: null }]);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Workflow className="size-4" /> {t("admin:agents.steps.title")}</CardTitle>
            <CardDescription>{t("admin:agents.steps.hint")}</CardDescription>
          </div>
          {!readOnly ? <Button size="sm" variant="outline" onClick={add} data-testid="add-step"><Plus /> {t("admin:agents.steps.add")}</Button> : null}
        </CardHeader>
        <CardContent className="space-y-3">
          {steps.length === 0 ? <EmptyState icon={Workflow} title={t("admin:agents.steps.empty")} description={t("admin:agents.steps.emptyBody")} /> : null}
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={i} className="rounded-xl border border-sand-200 bg-sand-50/60 p-3" data-testid={`step-${i}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-aeblack-900 text-[11px] font-semibold text-white">{i + 1}</span>
                  <Input value={s.title} readOnly={readOnly} onChange={(e) => edit(i, { title: e.target.value }, true)} className="h-8 flex-1" data-testid={`step-title-${i}`} />
                  {s.delegateAgentKey ? <Badge variant="ai" size="sm"><Bot /> {t("admin:agents.steps.handedTo", { agent: agents.find((a) => a.key === s.delegateAgentKey)?.name ?? s.delegateAgentKey })}</Badge> : null}
                  {!readOnly ? (
                    <div className="flex gap-0.5">
                      <Button size="icon-sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t("admin:agents.steps.moveUp")}><ArrowUp /></Button>
                      <Button size="icon-sm" variant="ghost" disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label={t("admin:agents.steps.moveDown")}><ArrowDown /></Button>
                      <Button size="icon-sm" variant="ghost" onClick={() => set(steps.filter((_, j) => j !== i))} aria-label={t("admin:agents.steps.remove")}><Trash2 /></Button>
                    </div>
                  ) : null}
                </div>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_240px]">
                  <Field label={t("admin:agents.steps.instruction")}>
                    <Textarea value={s.instruction} readOnly={readOnly} dir="auto" onChange={(e) => edit(i, { instruction: e.target.value }, true)} className="min-h-[88px] text-[13px]" placeholder={t("admin:agents.steps.instructionPlaceholder")} data-testid={`step-instruction-${i}`} />
                  </Field>
                  <div className="space-y-3">
                    <Field label={t("admin:agents.steps.doneBy")}>
                      <NativeSelect value={s.delegateAgentKey ?? ""} disabled={readOnly} onChange={(e) => edit(i, { delegateAgentKey: e.target.value || null })} data-testid={`step-delegate-${i}`}>
                        <option value="">{t("admin:agents.steps.thisAgent")}</option>
                        {agents.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
                      </NativeSelect>
                    </Field>
                    {!s.delegateAgentKey ? (
                      <Field label={t("admin:agents.steps.tools")} hint={t("admin:agents.steps.toolsHint")}>
                        <RefPicker options={tools} value={s.toolKeys} onChange={(v) => edit(i, { toolKeys: v })} readOnly={readOnly} maxHeight="max-h-40" />
                      </Field>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {steps.length > 0 ? <p className="rounded-lg border border-dashed border-sand-300 p-2 text-xs text-muted-foreground">{t("admin:agents.steps.final", { n: steps.length + 1 })}</p> : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Bot className="size-4" /> {t("admin:agents.steps.delegates")}</CardTitle>
          <CardDescription>{t("admin:agents.steps.delegatesHint")}</CardDescription>
        </CardHeader>
        <CardContent data-testid="agent-delegates">
          <RefPicker options={agents} value={body.delegateAgentKeys} onChange={(v) => patch({ delegateAgentKeys: v })} readOnly={readOnly} maxHeight="max-h-96" />
        </CardContent>
      </Card>
    </div>
  );
}
